import apiClient from './client';

// ─── Sicherheit, Verlauf und Betriebszugänge ────────────────────────────────
//
// Alle Zahlen stammen aus dem Protokoll. Die Antworten tragen deshalb die
// Aufbewahrungsfrist mit: was älter ist, wurde gelöscht und fehlt hier – das
// muss die Seite sagen können, sonst liest man eine Lücke als Ruhe.

export interface Sicherheitslage {
  aufbewahrungTage: number;
  aeltesterEintrag: string | null;
  fehlversuche: { letzte24h: number; letzte7d: number; letzte30d: number };
  abgewiesen: { letzte24h: number; letzte7d: number };
  ausgesperrt: { letzte24h: number; letzte7d: number };
  laufendeSperren: number;
  anmeldungen: { letzte24h: number; letzte7d: number };
  auffaelligeAdressen: { adresse: string; versuche: number; letzter: string }[];
  betroffeneKonten: { email: string; versuche: number; letzter: string }[];
  konten: { gesamt: number; mitZweiFaktor: number; deaktiviert: number };
  aktiveSitzungen: number;
  offeneBetriebszugaenge: number;
}

export async function fetchSicherheitslage(): Promise<Sicherheitslage> {
  const { data } = await apiClient.get<Sicherheitslage>('/admin/security');
  return data;
}

export interface Verlauf {
  aufbewahrungTage: number;
  jeTag: { tag: string; anmeldungen: number; sonstige: number }[];
  /** 24 Werte, Index = Stunde. */
  jeStunde: number[];
  /** 7 Werte, Index 0 = Montag. */
  jeWochentag: number[];
  haeufigsteAktionen: { action: string; anzahl: number }[];
  aktivsteKonten: { email: string; anzahl: number }[];
  grundgesamtheit: number;
}

export async function fetchVerlauf(): Promise<Verlauf> {
  const { data } = await apiClient.get<Verlauf>('/admin/activity');
  return data;
}

// ─── Betriebszugänge ────────────────────────────────────────────────────────

export interface Betriebszugang {
  id: string;
  treeId: string;
  treeName: string;
  userEmail: string;
  reason: string;
  grantedAt: string;
  expiresAt: string;
  revokedAt: string | null;
  aktiv: boolean;
}

export async function fetchZugaenge(nurAktive = false): Promise<{
  data: Betriebszugang[];
  maxStunden: number;
}> {
  const { data } = await apiClient.get<{ data: Betriebszugang[]; maxStunden: number }>(
    '/admin/tree-access',
    { params: nurAktive ? { active: 'true' } : {} },
  );
  return data;
}

export async function nimmZugang(input: {
  treeId: string;
  reason: string;
  stunden: number;
}): Promise<{ id: string; expiresAt: string }> {
  const { data } = await apiClient.post<{ id: string; expiresAt: string }>(
    '/admin/tree-access',
    input,
  );
  return data;
}

export async function beendeZugang(id: string): Promise<void> {
  await apiClient.delete(`/admin/tree-access/${id}`);
}

// ─── Anmeldesperren ─────────────────────────────────────────────────────────
//
// Die Zähler liegen in der Datenbank, nicht im Arbeitsspeicher eines Prozesses.
// Nur deshalb kann die Konsole überhaupt etwas aufheben, was in der Anwendung
// entstanden ist – und nur deshalb übersteht eine Sperre einen Neustart.

export interface Anmeldesperre {
  /** Vollständiger Schlüssel inklusive Präfix – zum Aufheben nötig. */
  key: string;
  /** 'login', 'webauthn' oder 'refresh'. */
  art: string;
  adresse: string;
  /** Port, auf dem die Anfrage einging – trennt Anwendung und Konsole. */
  port: string;
  versuche: number;
  laeuftAbAm: string;
  /** Grenze überschritten, also tatsächlich ausgesperrt. */
  gesperrt: boolean;
}

export async function fetchSperren(): Promise<{
  data: Anmeldesperre[];
  grenzen: Record<string, number>;
}> {
  const { data } = await apiClient.get<{ data: Anmeldesperre[]; grenzen: Record<string, number> }>(
    '/admin/lockouts',
  );
  return data;
}

export async function hebeSperreAuf(key: string): Promise<void> {
  await apiClient.delete(`/admin/lockouts/${encodeURIComponent(key)}`);
}

export async function hebeAlleSperrenAuf(): Promise<{ anzahl: number }> {
  const { data } = await apiClient.delete<{ anzahl: number }>('/admin/lockouts');
  return data;
}

// ─── Konten sperren ─────────────────────────────────────────────────────────

export async function sperreKonto(id: string, reason: string): Promise<void> {
  await apiClient.post(`/admin/users/${id}/block`, { reason });
}

export async function entsperreKonto(id: string): Promise<void> {
  await apiClient.post(`/admin/users/${id}/unblock`, {});
}
